/**
 * Die verbliebenen Termine des Abschnitts `main-data` - Portierung von `index.html:8447-8452`:
 * Bestelldatum und ETD (PI).
 *
 * UMGEZOGEN (Auftrag „Reiter Spedition", 2026-08-20): die uebrigen sechs Felder dieses Abschnitts
 * - ETD (tatsächlich), ETA (erwartete Ankunft), vsl. LT 74722 Buchen, Aufschlag auf ETA (Tage),
 * Container, Container-Volumen (m³) - stehen seither im neuen Reiter „Spedition"
 * (`Drawer/ShipTab/Transport.tsx`, Abschnitt `ship-transport`), wortgleich uebernommen samt der
 * `patchEta`/`patchEtaBookingOffset`/`setContainerType`-Ableitungen. Mit ihnen ist auch
 * Verhaltensaenderung 5 (DSV-gefuehrte Felder bei verknuepfter Sendung) dorthin umgezogen und dort
 * NEU verankert - an der tatsaechlich verknuepften Sendung (`sendung !== null`) statt an einem
 * (damals stets leeren) Sendungs-Array im Store, siehe Kopfkommentar von `Transport.tsx`. Die
 * damalige Aufloesefunktion darueber (inzwischen ersatzlos entfernt, Auftrag „Sendungsabruf beim
 * App-Start entfernen", 2026-08-22 - siehe `lib/suche.ts`), `CONTAINER_TYPES`,
 * `estimatedBuchenDateFromEta` und die ganze DSV-Verzweigung gehoeren deshalb NICHT mehr hierher.
 * Ausfuehrlich: `docs/superpowers/decisions/2026-08-20-reiter-spedition.md`.
 *
 * „ETD (PI)" bleibt BEWUSST im Reiter „Bestellung": es ist die Zusage aus der Proforma, keine
 * Speditionsmeldung - anders als „ETD (tatsächlich)", das die Sendung fuehrt (siehe `Transport.tsx`,
 * Abschnitt „WELCHES ETD?").
 *
 * Wie `Stammdaten.tsx` zeichnet diese Komponente ein Fragment ohne eigene Huelle: beide Felder
 * liegen im Original im selben `.field-grid` wie die Bestelldaten und die Notizen.
 */
import { Datumsfeld } from "../Datumsfeld.tsx";
import type { Order } from "../../../lib/types.ts";
import { feldZustandClass } from "../../../utils/feldZustand.ts";

export interface TermineProps {
  bestellung: Order;
  onAendern: (id: string, aenderung: Record<string, unknown>) => void;
}

export function Termine({ bestellung: o, onAendern }: TermineProps) {
  const id = o.id as string;
  const patch = (feld: string, wert: unknown): void => onAendern(id, { [feld]: wert });

  return (
    <>
      <div className="field">
        <label>Bestelldatum</label>
        {/*
         * Read-only seit Auftrag "Bestellfelder nach Anlage sperren" (2026-08-25): das Datum wird bei
         * der Anlage gesetzt und ist danach unveraenderlich (backend/src/domaene/feldgruppen.ts,
         * NICHT_SCHREIBBAR) - bei einer JTL-verknuepften Bestellung zeigt es zusaetzlich live das
         * JTL-Anlagedatum (ladeBestellung(), domaene/orders.ts), nicht den evtl. abweichenden
         * Hub-Wert. Dasselbe Muster wie `estimatedDeliveryBuchen` in `ShipTab/Transport.tsx`:
         * `readOnly` + `aria-readonly` auf einem `type="date"`-Feld. Nur der `orderDate`-Patch-
         * Aufruf entfaellt hier - `patch(...)` selbst bleibt fuer „ETD (PI)" unten weiter in Gebrauch.
         */}
        {/*
         * Auftrag 8 (strukturell statt Sibling-Element): kein `.feld-lock-anchor`-Span mehr - das
         * Schloss-Symbol kommt seither als CSS `background-image` direkt ueber `.feld-gesperrt`
         * (`styles/neubau/feldzustand.css`).
         */}
        <input
          className={feldZustandClass({ gesperrt: true })}
          type="date"
          value={(o.orderDate as string) ?? ""}
          readOnly
          aria-readonly="true"
          title="Bestelldatum kommt aus JTL und ist nach Anlage nicht änderbar."
        />
      </div>
      <div className="field">
        <label>ETD (PI)</label>
        <Datumsfeld
          wert={(o.etdPi as string) ?? ""}
          onCommit={(wert) => patch("etdPi", wert)}
          className={feldZustandClass({ erwartet: true, leer: !(o.etdPi as string) })}
        />
      </div>
    </>
  );
}
